/**
 * @file Shared frontend helpers for app-owned branding CSS variables.
 */

export const DEFAULT_BRANDING = Object.freeze({
  primaryColor: "#ffd612",
  primaryHoverColor: "#e6c010",
  accentColor: "#f78183",
  secondaryAccentColor: "#83ffde",
  surfaceTintOpacity: 0.1,
});

export const SURFACE_TINT_MIN_OPACITY = 0.04;
export const SURFACE_TINT_MAX_OPACITY = 0.4;

/**
 * Returns a valid hex colour or the provided fallback.
 * @param {*} value Candidate colour value.
 * @param {string} fallback Fallback hex colour.
 * @returns {string} Normalized six-digit hex colour.
 */
export function normalizeHexColor(value, fallback) {
  const color = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(color)) {return color;}
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
  }
  return fallback;
}

/**
 * Clamps brand tint opacity to the approved safe range.
 * @param {*} value Candidate opacity.
 * @returns {number} Safe opacity value.
 */
export function clampSurfaceTint(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {return DEFAULT_BRANDING.surfaceTintOpacity;}
  return Math.min(
    SURFACE_TINT_MAX_OPACITY,
    Math.max(SURFACE_TINT_MIN_OPACITY, numberValue),
  );
}

/**
 * Converts a hex colour to RGB channels.
 * @param {string} hex Hex colour.
 * @returns {{red: number, green: number, blue: number}} RGB channels.
 */
export function hexToRgb(hex) {
  const color = normalizeHexColor(hex, DEFAULT_BRANDING.primaryColor);
  return {
    red: parseInt(color.slice(1, 3), 16),
    green: parseInt(color.slice(3, 5), 16),
    blue: parseInt(color.slice(5, 7), 16),
  };
}

/**
 * Returns readable text colour for the provided background colour.
 * @param {string} hex Hex background colour.
 * @returns {string} Contrast text colour.
 */
export function getContrastColor(hex) {
  const {red, green, blue} = hexToRgb(hex);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.62 ? "#111827" : "#ffffff";
}

/**
 * Builds an rgba surface tint from a hex colour.
 * @param {string} color Hex source colour.
 * @param {number} opacity Tint opacity.
 * @returns {string} CSS rgba colour.
 */
export function getTintedSurface(color, opacity) {
  const rgb = hexToRgb(color);
  return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${opacity})`;
}

/**
 * Resolves approved branding tokens from app data, config, or a branding map.
 * @param {object} [source] appData, config, or branding token source.
 * @returns {object} Normalized branding tokens.
 */
export function getBrandingTokens(source = {}) {
  const branding = source.config?.branding || source.branding || source || {};
  return {
    primaryColor: normalizeHexColor(branding.primaryColor, DEFAULT_BRANDING.primaryColor),
    primaryHoverColor: normalizeHexColor(
      branding.primaryHoverColor,
      DEFAULT_BRANDING.primaryHoverColor,
    ),
    accentColor: normalizeHexColor(branding.accentColor, DEFAULT_BRANDING.accentColor),
    secondaryAccentColor: normalizeHexColor(
      branding.secondaryAccentColor,
      DEFAULT_BRANDING.secondaryAccentColor,
    ),
    surfaceTintOpacity: clampSurfaceTint(branding.surfaceTintOpacity),
  };
}

/**
 * Applies approved branding CSS variables to a container.
 * @param {HTMLElement} container Container that owns branded app UI.
 * @param {object} [source] appData, config, or branding token source.
 * @returns {object} Normalized branding tokens that were applied.
 */
export function applyBrandingVariables(container, source = {}) {
  const branding = getBrandingTokens(source);
  container.style.setProperty("--dfc-brand-primary", branding.primaryColor);
  container.style.setProperty(
    "--dfc-brand-primary-contrast",
    getContrastColor(branding.primaryColor),
  );
  container.style.setProperty("--dfc-brand-primary-hover", branding.primaryHoverColor);
  container.style.setProperty("--dfc-brand-accent", branding.accentColor);
  container.style.setProperty("--dfc-brand-secondary-accent", branding.secondaryAccentColor);
  container.style.setProperty(
    "--dfc-brand-primary-ring",
    getTintedSurface(branding.primaryColor, 0.3),
  );
  container.style.setProperty(
    "--dfc-brand-surface-tint",
    getTintedSurface(branding.primaryColor, branding.surfaceTintOpacity),
  );
  return branding;
}
